/**
 * AI inventory analysis result notifications (completed/failed).
 *
 * Both forms are required: database-fetched notifications carry the payload's
 * short type, but on the broadcast path Laravel's BroadcastNotificationCreated
 * merges `type => <notification FQCN>` OVER the payload, clobbering it.
 */
const AI_INVENTORY_TYPES = [
    'inventory_ai_analysis_completed',
    'inventory_ai_analysis_failed',
    'App\\Notifications\\InventoryAiAnalysisCompletedNotification',
    'App\\Notifications\\InventoryAiAnalysisFailedNotification',
];

/**
 * Whether a notification is an AI inventory analysis result. These carry
 * `move_id` + `ai_job_id` and are the one terminal signal that reaches the
 * user on any page, so they also drive the processing banner's bookkeeping.
 *
 * @param {Object} notification - Notification object with data property
 * @returns {boolean}
 */
export function isAiInventoryNotification(notification) {
    return AI_INVENTORY_TYPES.includes(notification?.data?.type);
}

/**
 * Sticker PDF generation result notifications (generated/failed).
 *
 * Both forms are required for the same reason as AI_INVENTORY_TYPES above:
 * BroadcastNotificationCreated clobbers the payload's short type with the
 * notification FQCN on the broadcast path.
 */
const STICKER_GENERATION_TYPES = [
    'stickers_generated',
    'stickers_generation_failed',
    'App\\Notifications\\StickersGeneratedNotification',
    'App\\Notifications\\StickersGenerationFailedNotification',
];

/**
 * Whether a notification is a sticker generation result. These carry `order_id`
 * + `sticker_count` and are the one terminal signal that reaches the user on
 * any page, so they also settle the uploads-list skeleton's bookkeeping.
 *
 * @param {Object} notification - Notification object with data property
 * @returns {boolean}
 */
export function isStickerGenerationNotification(notification) {
    return STICKER_GENERATION_TYPES.includes(notification?.data?.type);
}

/**
 * Whether a notification's target route is the page the user is already on.
 * Used to avoid rendering a "View order" action that navigates nowhere.
 *
 * @param {Object|null} route - Route object from buildNotificationRoute()
 * @param {Object|null} currentRoute - The router's current route
 * @returns {boolean}
 */
export function isNotificationRouteCurrent(route, currentRoute) {
    if (!route?.name || !currentRoute || route.name !== currentRoute.name) {
        return false;
    }

    const targetId = route.params?.id;

    if (targetId == null) {
        return true;
    }

    // Route params are strings while notification ids are numeric — compare as strings.
    return String(currentRoute.params?.id ?? '') === String(targetId);
}

/**
 * Notification action types and their corresponding routes
 */
const NOTIFICATION_ACTIONS = {
    VIEW_SCHEDULER: {
        routeName: 'DiaryScheduler',
        buildParams: (data) => ({
            start_datetime: data.action_params?.start_datetime,
            end_datetime: data.action_params?.end_datetime,
        }),
    },
    // Future actions can be added here
    // VIEW_ORDER: { routeName: 'OrderView', ... }
};

/**
 * Build a route object from notification data
 * Supports route-name-based, action-based, and URL-based notifications
 *
 * @param {Object} notification - Notification object with data property
 * @returns {Object|null} Route object for router.push() or null if no route
 */
export function buildNotificationRoute(notification) {
    // Route-name-based routing (preferred format)
    if (notification.data.route) {
        const route = {
            name: notification.data.route,
            params: notification.data.route_params || {},
        };

        if (notification.data.route_query) {
            route.query = notification.data.route_query;
        }

        return route;
    }

    // Legacy action-based routing
    const action = notification.data.action;
    if (action && NOTIFICATION_ACTIONS[action]) {
        const config = NOTIFICATION_ACTIONS[action];
        return {
            name: config.routeName,
            query: config.buildParams(notification.data),
        };
    }

    // Fallback to legacy URL-based routing (OLD FORMAT)
    if (notification.data.url) {
        try {
            let url;
            // Handle both absolute URLs and relative paths
            if (notification.data.url.startsWith('http://') || notification.data.url.startsWith('https://')) {
                url = new URL(notification.data.url);
            } else {
                url = new URL(notification.data.url, window.location.origin);
            }

            const queryParams = {};
            url.searchParams.forEach((value, key) => {
                queryParams[key] = value;
            });

            return {
                path: url.pathname,
                query: queryParams,
            };
        } catch {
            // Fallback: manual parsing
            const urlParts = notification.data.url.split('?');
            const path = urlParts[0];
            const queryParams = {};

            if (urlParts[1]) {
                const params = new URLSearchParams(urlParts[1]);
                params.forEach((value, key) => {
                    queryParams[key] = value;
                });
            }

            return {
                path: path,
                query: queryParams,
            };
        }
    }

    return null;
}
